import { Component, OnDestroy, ViewChild } from '@angular/core';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { Events, NavController, NavParams } from 'ionic-angular';
import { LockedDayService } from '../../../../services/locked-day/locked-day.service';
import { DateService } from '../../../../services/date/date.service';
import { DailyLogService } from '../../../../services/daily-log/daily-log.service';
import { AppDataSyncService } from '../../../../services/app-data-sync/app-data-sync.service';
import { VesselCrewOvertimeEditPage } from '../vessel-crew-overtime-edit/vessel-crew-overtime-edit';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { LockDayFilters } from '../../../../models/lock-day.models';
import {
  ActionType,
  ErrorType,
  ValidationError,
  PageValidationType,
  ValidationResult
} from '../../../../models/projects/validation.models';
import { VesselCrewEditor, VesselCrewOvertimeEditor } from '../../../../models/vessel-crew/vessel-crew.models';
import { VesselCrewService } from '../../../../services/vessel-crew/vessel-crew.service';
import { EnumSummary } from '../../../../models/base/base.model';
import { catchError, debounce } from 'rxjs/operators';
import { timer } from 'rxjs/observable/timer';
import { LockedDayPermissions, VesselCrewPermissions } from '../../../../models/permissions/permission.models';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { GridComponent } from '@progress/kendo-angular-grid';
import { vesselOvertimeColumns } from './vessel-crew-overtime-list.config';
import { CompleteDayService } from '../../../../services/complete-day/complete-day.service';
import { NavHelperService } from '../../../../services/nav/nav-helper.service';
import { cloneDeep } from 'lodash';
import { ImplementerService } from '../../../../services/implementer/implementer.service';
import { DailyLogDatePickerComponent } from '../../../shared/components/daily-log-date-picker/daily-log-date-picker';
import { AppConfig } from '../../../../app/app.config';

@Component({
  selector: 'app-vessel-crew-edit-component',
  templateUrl: 'vessel-crew-edit-component.component.html'
})
export class VesselCrewEditComponentComponent implements OnDestroy {
  @ViewChild('vesselCrewGrid') grid?: GridComponent;
  @ViewChild('dailyLogDatePickerComponent') dailyLogDatePickerComponent: DailyLogDatePickerComponent;

  config = AppConfig.settings;
  vesselCrewEditor: VesselCrewEditor = new VesselCrewEditor();
  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  validationResult: ValidationResult = new ValidationResult();

  $saveRequestedChange: Subject<boolean> = new Subject<boolean>();
  saveDebounceSubscription: Subscription;

  loading: boolean;
  validationloading: boolean;
  savingVesselCrew: boolean = false;
  private pendingVesselCrewSave: boolean = false;
  private loadVersion = 0;

  dayLockedByDate: boolean = false;
  lockable: boolean = false;
  private overtimeSummaries: EnumSummary[];
  dayCompleted: boolean;
  isImplementerActiveForDay: boolean;

  permissions: VesselCrewPermissions = new VesselCrewPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();

  firstLoadComplete: boolean = false;
  dateBeforeInitialSetup: boolean;

  // Grid variables
  columns = vesselOvertimeColumns;
  currentSetupConfigured: boolean;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public lockedDayService: LockedDayService,
    public dateService: DateService,
    public dailyLogService: DailyLogService,
    public vesselCrewService: VesselCrewService,
    private appDataSyncService: AppDataSyncService,
    public events: Events,
    private permissionService: PermissionsService,
    private completeDayService: CompleteDayService,
    private navHelperService: NavHelperService,
    private implementerService: ImplementerService,
  ) {

    this.events.subscribe('saveVessel', async () => {
      await this.saveVesselCrewData(false);
    });

    this.vesselCrewService.getOvertimeRequestedBys().then(value => {
      this.overtimeSummaries = value;
    });

    this.navHelperService.currentSetupConfigured.subscribe(currentSetupConfigured => {
      this.currentSetupConfigured = currentSetupConfigured;
    });
  }

  async ngOnInit() {
    this.loading = true;
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    await this.ionViewEnter();

    await this.updateDataEntryVariables();
  }

  async ionViewEnter() {
    this.validationloading = true;

    await this.loadData(true);
    this.setupAutoSaveDebounce();

    this.permissions = await this.permissionService.vesselCrewPermissions();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);
  }

  ngOnDestroy(): void {
    // without unsubscribing to this, old values that were published previously will still be held, causing errors if those values no longer exist
    this.events.unsubscribe('saveVessel');
    if (this.saveDebounceSubscription) {
      this.saveDebounceSubscription.unsubscribe();
    }
  }

  // one method for all - that checks dayCompleted, implementer status, etc, and sets canEnterData flag
  async updateDataEntryVariables() {
    await Promise.all([
      this.checkImplementerActive(),
      this.checkDayLocked(),
      this.permissionService.updateLockedDayFunctionalPermissions(this.lockedDayPermissions, this.dailyLogDate)
    ]);

    await this.setCanEnterDataFlag();
  }

  async setCanEnterDataFlag() {
    if (!this.dayCompleted
      && ((this.config.isImplementerEnvironment && this.isImplementerActiveForDay) || (!this.config.isImplementerEnvironment && this.dayLockedByDate))) {
      this.navHelperService.updateCanEnterData(true);
    } else {
      this.navHelperService.updateCanEnterData(false);
    }
  }

  setupAutoSaveDebounce() {
    if (this.saveDebounceSubscription) {
      this.saveDebounceSubscription.unsubscribe();
    }

    this.saveDebounceSubscription = this.$saveRequestedChange
      .pipe(
        catchError(err => {
          throw (err.error);
        }),
        debounce(() => timer(500)))
      .subscribe(val => {
        this.validationloading = true;
        this.saveVesselCrewData(val);
      });
  }

  async checkDayLocked() {
    const filters: LockDayFilters = {
      date: this.dailyLogDate.dateTime,
      userId: null,
      history: null
    };

    const lockedSummary = await this.lockedDayService.checkDay(filters);

    this.dayLockedByDate = lockedSummary.lockedByDate;
    this.lockable = lockedSummary.lockable;

    this.completeDayService.checkDay(filters).then(result => {
      this.dayCompleted = result;
    });
  }

  async dayUnlockedForEditing() {
    this.loading = true;
    await this.checkDayLocked();
    this.loading = false;
  }

  async changesCancelled() {
    this.firstLoadComplete = false;
    this.loading = true;
    await this.loadData(true);
  }

  async changesSaved() {
    await this.loadData(false);
  }

  queueAutoSave(isComplete: boolean) {
    if (this.loading || !this.firstLoadComplete || this.dailyLogDatePickerComponent?.dateChangeLoading) {
      return;
    }

    this.$saveRequestedChange.next(isComplete);
  }

  public async saveVesselCrewData(isComplete: boolean) {
    if (this.savingVesselCrew) {
      this.pendingVesselCrewSave = true;
      return;
    }

    const editor = this.vesselCrewEditor;
    const version = this.loadVersion;
    const isCurrentEditor = () => this.loadVersion === version && this.vesselCrewEditor === editor;
    this.validationloading = true;
    this.savingVesselCrew = true;

    if ((!this.config.isImplementerEnvironment && !this.dayLockedByDate)
      || ((this.config.isImplementerEnvironment && !this.isImplementerActiveForDay))) {
      this.validationloading = false;
      this.savingVesselCrew = false;
      return;
    }

    try {
      const editorToSave = cloneDeep(editor);
      editorToSave.date = cloneDeep(editor.date || this.dailyLogDate);
      editorToSave.isCompleted = isComplete;

      const savedEditor = await this.vesselCrewService.saveEditor(editorToSave);
      if (isCurrentEditor()) {
        editor.id = savedEditor.id;
        editor.isCompleted = isComplete;
        editor.overtime = savedEditor.overtime ?? [];
        this.validationResult = new ValidationResult();
      }

      await this.vesselCrewService.saveVesselCrewChangeMultipleRecords([isCurrentEditor() ? editor : savedEditor]);
    } catch (error) {
      if (this.isValidationSaveError(error)) {
        if (isCurrentEditor()) {
          this.validationResult = new ValidationResult();
          this.applyValidationErrors(error);
          editor.isCompleted = false;
        }
      } else {
        throw error;
      }
    } finally {
      if (isCurrentEditor()) {
        this.validationloading = false;
      }
      this.savingVesselCrew = false;

      if (this.pendingVesselCrewSave) {
        this.pendingVesselCrewSave = false;
        await this.saveVesselCrewData(false);
      }
    }
  }

  markVesselCrewComplete() {
    this.saveVesselCrewData(true);
  }

  getEnumName(id: string): string {
    return this.overtimeSummaries.find(value => value.id === id).name;
  }

  async loadData(refresh: boolean, dateChange?: boolean) {
    if (refresh) {
      this.loadVersion++;
      this.loading = true;
      this.validationloading = true;
    }

    // refresh daily log date, if user has changed the date
    if (dateChange) {
      this.firstLoadComplete = false;
      this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
      await this.updateDataEntryVariables();
    }

    await this.checkDayLocked();

    if (refresh) {
      await this.appDataSyncService.updateAppDataForDate(this.dailyLogDate);
      if (this.grid) {
        this.grid.loading = true;
      }

      this.vesselCrewEditor = await this.vesselCrewService.getVesselCrewDataForDay(this.dailyLogDate);
      try {
        await this.vesselCrewService.validate(this.vesselCrewEditor).toPromise();
        this.validationResult = new ValidationResult();
      } catch (error) {
        if (!this.isValidationSaveError(error)) {
          throw error;
        }
        this.validationResult = new ValidationResult();
        this.applyValidationErrors(error);
        this.vesselCrewEditor.isCompleted = false;
      } finally {
        // Release the retained day and toolbar only after validation settles.
        this.firstLoadComplete = true;
        this.validationloading = false;
        this.loading = false;
        if (this.grid) {
          this.grid.loading = false;
        }
      }
    } else {
      this.validationloading = false;
      this.loading = false;
    }
  }

  editOvertime(editor: VesselCrewOvertimeEditor) {
    this.navCtrl.push(VesselCrewOvertimeEditPage, {
      editor: editor,
      model: this.vesselCrewEditor,
      callback: this.saveVesselCrewData,
      isCreate: false
    });
  }

  addOvertime() {
    const editor = new VesselCrewOvertimeEditor();
    this.navCtrl.push(VesselCrewOvertimeEditPage, {
      editor: editor,
      model: this.vesselCrewEditor,
      callback: this.saveVesselCrewData,
      isCreate: true
    });
  }

  removeOvertime(editor: VesselCrewOvertimeEditor) {
    const index = this.vesselCrewEditor.overtime.indexOf(editor);

    if (index === -1) {
      return;
    }

    this.vesselCrewEditor.overtime.splice(index, 1);
    this.saveVesselCrewData(true);
  }

  otherPagesNotComplete(): boolean {
    return this.validationResult.errors.some(e => e.pageValidationType != null
      && e.pageValidationType !== PageValidationType.DailyObservations
      && e.pageValidationType !== PageValidationType.FieldPositioning
      && e.pageValidationType !== PageValidationType.VoyageLog);
  }

  // isImplementerEnvironment variable only tells us if the environment is the implementer, we still need to check if current date falls between
  // snapshotSummary start and end dates, if it does then we know this date is editable
  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  dateBeforeInitialSetupChanged($event: boolean) {
    this.dateBeforeInitialSetup = $event;
  }

  goToActiveLogDay() {
    this.dailyLogDatePickerComponent.goToActiveLogDay();
  }

  dateLoadingStarted() {
    this.loadVersion++;
    this.loading = true;
    this.validationloading = true;
    this.firstLoadComplete = false;
  }

  dateLoadingFinished() {
    this.firstLoadComplete = true;
  }

  get showPobStructure(): boolean {
    return !!this.vesselCrewEditor?.usePobStructure;
  }

  get totalPobToDisplay(): number {
    if (!this.vesselCrewEditor) {
      return 0;
    }

    if (this.showPobStructure) {
      return (+this.vesselCrewEditor.seajacksPersonnel || 0)
        + (+this.vesselCrewEditor.cadelerContractors || 0)
        + (+this.vesselCrewEditor.clientPersonnel || 0);
    }

    return (+this.vesselCrewEditor.seajacksPersonnel || 0)
      + (+this.vesselCrewEditor.clientPersonnel || 0)
      + (+this.vesselCrewEditor.cateringPersonnel || 0)
      + (+this.vesselCrewEditor.otherPersonnel || 0);
  }

  isFieldRequired(field: string): boolean {
    switch (field) {
      case 'numberOfFlights':
      case 'numberOfCrewBoats':
      case 'personnelOn':
      case 'personnelOff':
      case 'seajacksPersonnel':
      case 'clientPersonnel':
        return true;
      case 'cadelerContractors':
        return this.showPobStructure;
      case 'cateringPersonnel':
      case 'otherPersonnel':
        return !this.showPobStructure;
      default:
        return false;
    }
  }

  fieldRowClasses(field: string) {
    return {
      'validation-failed': this.hasValidationError(field)
    };
  }

  private isValidationSaveError(error: any): boolean {
    return (error?.status === 412 || error?.status === 400) && !!error?.error?.errors?.length;
  }

  private applyValidationErrors(error: any) {
    const errors = (error?.error?.errors || []) as ValidationError[];

    this.validationResult.errors = this.validationResult.errors.concat(
      errors.filter(p =>
        this.validationResult.errors.filter(er =>
          er.message === p.message).length === 0));

    this.validationResult.errors.forEach(vesselCrewError => {
      if (vesselCrewError.pageValidationType == null) {
        vesselCrewError.pageValidationType = PageValidationType.VesselCrew;
        vesselCrewError.type = ErrorType.Error;
        vesselCrewError.actionType = ActionType.VesselCrew;
      }
    });

    this.validationResult.isValid = error?.error?.isValid === undefined ? false : error.error.isValid;
  }

  private hasValidationError(field: string): boolean {
    const expectedProperty = this.normalizeValidationValue(field);

    return (this.validationResult?.errors || []).some(error =>
      this.normalizeValidationValue(error?.property) === expectedProperty);
  }

  private normalizeValidationValue(value: string): string {
    return (value || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  }
}



// WEBPACK FOOTER //
// ./src/pages/DPR Pages/vessel-crew/vessel-crew-edit-component/vessel-crew-edit-component.component.ts